/**
 * Block store — the shared block-envelope cache + write surface.
 *
 * `blockStore` is the **single source of truth** for block envelope
 * data across the entire viewer. Every per-scope view (homepage,
 * collection detail, stack, group, smart collection, card previews,
 * pinned section) holds only `(scopeId, blockId, sortKey)` pointer
 * rows in its own store and resolves envelopes via `useById`.
 *
 * Reads:
 *   blockStore.useById(id)           — reactive single lookup with network fallback
 *   blockStore.get(id)               — synchronous one-shot read (write paths only)
 *
 * Cache writes (intended for any code outside this file that needs to
 * land an envelope into the cache — search results, smart-collection
 * fetches, manual prefetches):
 *   blockStore.upsert(envelope)
 *   blockStore.upsertMany(envelopes)
 *   blockStore.delete(id)
 *   blockStore.cacheEnvelopes(envelopes)   — batched wrapper around upsertMany
 *   blockStore.ensureLoaded(blockIds)      — fill cache on demand
 *
 * Domain writes (orchestrate envelope cache + per-scope index stores +
 * server):
 *   blockStore.add({ content?, files?, collectionIds? })
 *   blockStore.surface(blockId)
 *   blockStore.remove(blockId)
 *
 * The homepage timeline's pagination state (segments, "load more"
 * cursor, jump-to-date, gap loading) lives in `mainStreamStore` — see
 * the file header there for the rationale. Per-collection block
 * indices live in `collectionStore.useBlocks`. Pinned section lives
 * in `pinnedStreamStore`. block-store is purely a key-value cache;
 * every other store tracks which keys belong to which scope.
 */
import { generateId } from "@shared/types/id";
import { useLiveQuery, eq } from "@tanstack/react-db";
import { fetchBlockEnvelope } from "@/api/query-api";
import { blockCollection } from "@/data/block-collection";
import { collectionStore } from "@/data/collection-store";
import { mainStreamStore } from "@/data/main-stream-store";
import { queryClient } from "@/data/query-client";
import { ACCOUNT_ME_QUERY_KEY } from "@/hooks/use-account-me";
import { Logger } from "@/lib/logger";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_CONTENT_LENGTH = 100_000;
const MAX_FILES = 10;
const DEFAULT_CHANNEL = "viewer_client";
const MAX_ENSURE_LOADED = 10;
/** Scroll position when surfacing a card: leave this much space below the fixed top bar. */
const SURFACE_SCROLL_TOPBAR_OFFSET_PX = 100;
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
/**
 * Look up a single block envelope by id, reactively.
 *
 * Reads the local `blockCollection` (TanStack DB live query) only.
 * No per-block network fallback runs here.
 *
 * Why no `useQuery` fallback: the previous shape ran a per-card
 * `useQuery({ queryFn: fetchBlockEnvelope })` alongside the live
 * query, primed by `initialData` from whatever was already in
 * `blockCollection`. The intent was "fetch on cache miss, no-op on
 * cache hit". In practice, on cold homepage loads the live query
 * returns `[]` for one render (the bulk `listBlocks` hasn't resolved
 * yet), so `initialData` was `undefined` and every visible card fired
 * its own `/v1/block/{id}/details` request — 100+ redundant fetches
 * that raced the bulk seed and re-downloaded the same envelopes the
 * server was about to deliver in one payload, 12x heavier per block
 * (the details endpoint bundles tags/taxonomies/artifacts/etc).
 *
 * The live query alone is sufficient because:
 *   - Homepage / collection feeds populate `blockCollection`
 *     via their bulk `listBlocks(...)` seed before mounting cards.
 *   - Side-loaded screens that may not have the envelope (deep-linked
 *     detail view, search results, smart collections) explicitly call
 *     `blockStore.ensureLoaded([id])` themselves — see
 *     `BlockDetailViewContainer`'s `useEffect`.
 *   - Cross-device refresh (the original `refetchOnWindowFocus`
 *     argument for keeping the per-card query alive) is handled by
 *     re-running the bulk `listBlocks` on focus, not by re-fetching
 *     every visible card individually.
 */
function useById(id) {
    const { data: liveData } = useLiveQuery((q) => q.from({ m: blockCollection }).where(({ m }) => eq(m.block.id, id ?? "")), [id]);
    return id && liveData.length > 0 ? liveData[0] : undefined;
}
/**
 * Synchronous one-shot read. Used by write paths that need to derive a
 * sortKey from the cached envelope (e.g. `copyBlocks` looking up the
 * current `surfaceAt` to seed an optimistic membership row). Returns
 * `undefined` if the envelope hasn't been cached yet.
 */
function get(id) {
    return blockCollection.get(id);
}
// ---------------------------------------------------------------------------
// Cache writes (public)
// ---------------------------------------------------------------------------
/**
 * Insert or update a single envelope in the cache. Use this from any
 * caller outside `block-store.ts` that needs to land envelopes — it
 * means "this block exists; here's the latest data we have". Does not
 * touch any per-scope index store on its own.
 */
function upsert(envelope) {
    blockCollection.utils.writeUpsert(envelope);
}
/** Batched version of `upsert`. */
function upsertMany(envelopes) {
    if (envelopes.length === 0)
        return;
    blockCollection.utils.writeBatch(() => {
        for (const env of envelopes) {
            blockCollection.utils.writeUpsert(env);
        }
    });
}
/** Drop an envelope from the cache. Per-scope index cleanup is the caller's job. */
function deleteFromCache(id) {
    blockCollection.utils.writeDelete(id);
}
// ---------------------------------------------------------------------------
// Writes — optimistic capture
// ---------------------------------------------------------------------------
/**
 * Read the signed-in account's id synchronously from the React Query
 * cache. `useAccountMe`'s `queryFn` resolves through the bootstrap
 * singleton on cold load, so by the time the user can drive
 * `blockStore.add` (omnibar / drop zone / toolbar all require the app
 * shell to be mounted, which depends on bootstrap) the value is in
 * cache. Returns `undefined` only in pathological "submit before
 * bootstrap settled" races; callers tolerate the empty fallback.
 *
 * Threaded into `buildPlaceholderEnvelope` so the optimistic placeholder
 * carries the user's real `createdBy` — keeps attribution UI (avatars,
 * "you" labels, edit/delete affordances) consistent between the
 * placeholder and the server envelope that replaces it ~10 s later.
 */
function getCurrentAccountId() {
    const account = queryClient.getQueryData(ACCOUNT_ME_QUERY_KEY);
    return account ? account.id : undefined;
}
/**
 * Build an optimistic placeholder envelope under a stable `clientBlockId`.
 *
 * The id is generated by the caller (in `add` below) so the same value
 * is used for: (1) the cache write here, (2) the `clientBlockId`
 * parameter on the ingest API, and (3) the polling lookup. The server
 * persists the root block under that exact id, so the polling result is
 * just an envelope upsert under the same key — no delete + reinsert
 * dance, no membership row rewiring.
 *
 * If the input is a single URL we render a `link` placeholder so the
 * card shape matches the eventual real envelope (no jarring text→link
 * style swap when the server stub lands a moment later). Mixed text
 * (`"Check this out https://…"`) stays a `text` placeholder — the
 * server will treat the text as the root and the URL as a submemory.
 *
 * If the first dropped file is an image we render an `image` placeholder
 * pointing at a `blob:` URL of the local File. The user sees the actual
 * picture they just dropped — same card shape as the eventual real
 * envelope — instead of a text card titled `image.png`. The blob URL is
 * an `external` `FileReference`, which `resolveFileUrl` returns as-is,
 * so no shared-block changes are needed; when the server's real
 * `internal` FileReference lands, the upsert swaps the URL transparently.
 */
function buildPlaceholderEnvelope(id, content, files, doc) {
    const now = new Date().toISOString();
    const trimmed = content?.trim();
    const isUrlOnly = trimmed !== undefined && isLikelyUrl(trimmed);
    const preview = trimmed?.substring(0, 200) ?? files?.[0]?.name ?? "New block";
    const firstFile = files?.[0];
    const isImageDrop = firstFile !== undefined &&
        !doc &&
        !trimmed &&
        firstFile.type.startsWith("image/");
    let placeholderType;
    let placeholderContent;
    if (doc) {
        placeholderType = "text";
        placeholderContent = { text: preview, doc };
    }
    else if (isImageDrop) {
        placeholderType = "image";
        placeholderContent = {
            file: { type: "external", url: URL.createObjectURL(firstFile) },
        };
    }
    else if (isUrlOnly) {
        placeholderType = "link";
        placeholderContent = { subtype: "generic", generic: {}, url: trimmed };
    }
    else {
        placeholderType = "text";
        placeholderContent = { text: preview };
    }
    const block = {
        id,
        collectionId: "",
        createdBy: getCurrentAccountId() ?? "",
        lineageId: "",
        type: placeholderType,
        content: placeholderContent,
        title: preview,
        summary: "",
        metadata: { channel: "viewer_client" },
        ingestorVersion: "0",
        processingStatus: "pending",
        capturedAt: now,
        createdAt: now,
        // Placeholder mirrors `BlockEntity.create` — `surfaceAt` is the
        // canonical timeline-sort key and defaults to `now` on insert,
        // so the freshly-captured card sorts to the top of the homepage
        // feed (which orders on `surfaceAt desc`).
        surfaceAt: now,
    };
    return { block, _files: files };
}
/**
 * Cheap heuristic — match the omnibar's typical "paste a URL" shape
 * without pulling in a linkifier on the hot path. The server's
 * `linkifyjs` parse is the source of truth; this only governs the
 * placeholder's visual treatment for the few-hundred-millisecond
 * window before the server stub lands.
 */
function isLikelyUrl(value) {
    if (/\s/.test(value))
        return false;
    return /^https?:\/\/[^\s]+\.[^\s]+/i.test(value);
}
/**
 * Decode the dropped image to read its intrinsic dimensions and upsert
 * them onto the placeholder. `ImageBlockView` only applies the
 * `--fit-contain` variant (which clamps the photo to the card box) when
 * `width && height` are known; without them, large photos render at
 * natural size and overflow the card. The decode is fast (10s of ms for
 * typical photos) so the user sees the image fit correctly within a
 * frame or two of the card appearing.
 *
 * If the placeholder was already replaced by the real server envelope
 * by the time the decode finishes (very fast uploads), the upsert is a
 * no-op against the now-`internal` FileReference — we only patch when
 * the cached envelope is still our blob-URL placeholder.
 */
async function hydrateImagePlaceholder(blockId, file) {
    const dimensions = await decodeImageDimensions(file);
    if (!dimensions)
        return;
    const current = blockCollection.get(blockId);
    if (!current || current.block.type !== "image")
        return;
    const currentContent = current.block.content;
    if (currentContent.file?.type !== "external")
        return;
    blockCollection.utils.writeUpsert({
        ...current,
        block: {
            ...current.block,
            content: {
                ...currentContent,
                width: dimensions.width,
                height: dimensions.height,
            },
        },
    });
}
function decodeImageDimensions(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            }
            else {
                resolve(undefined);
            }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(undefined);
        };
        img.src = url;
    });
}
/**
 * Window after which the placeholder's `blob:` URL is revoked. Long
 * enough to comfortably outlast the upload + ingest + list-refetch
 * cycle that swaps the placeholder for the real `internal` FileReference
 * (typically <10s, occasionally up to a few minutes for slow scrapers),
 * so we never revoke a URL that's still being rendered. After this
 * window the placeholder is either gone or has been flipped to
 * `errored` by the terminal-state fallback below — in both cases the
 * blob URL is no longer referenced by the visible card.
 */
const PLACEHOLDER_BLOB_URL_REVOKE_DELAY_MS = 5 * 60_000;
/**
 * Revoke the `blob:` URL on a placeholder, if there's still one in the
 * cache. Called from two places: a delayed `setTimeout` that catches
 * the steady-state replacement-by-real-envelope case, and the immediate
 * failure path in `add()` when the upload request itself fails before
 * the placeholder is ever replaced.
 */
function revokePlaceholderBlobUrl(blockId) {
    const current = blockCollection.get(blockId);
    if (!current || current.block.type !== "image")
        return;
    const file = current.block.content.file;
    if (file?.type === "external" && file.url?.startsWith("blob:")) {
        URL.revokeObjectURL(file.url);
    }
}
function schedulePlaceholderBlobUrlRevoke(blockId) {
    setTimeout(() => {
        revokePlaceholderBlobUrl(blockId);
    }, PLACEHOLDER_BLOB_URL_REVOKE_DELAY_MS);
}
async function add(input, opts) {
    const { content, doc, files, collectionIds } = input;
    if (!content && !doc && (!files || files.length === 0))
        return;
    // First selected collection rides along on the ingest payload so the
    // server creates the block IN that collection. Additional collections
    // get the block via `copyBlocks` once the polling loop has the real
    // envelope.
    const primaryCollectionId = collectionIds?.[0];
    const additionalCollectionIds = collectionIds?.slice(1) ?? [];
    // The id is generated once and reused across every layer: cache,
    // ingest payload, polling, and per-scope index rows. The server's
    // create path accepts the `clientBlockId` and uses it as the
    // persisted block id, so placeholder and real envelope live under
    // the same key — no membership row ever needs to be rewritten.
    const clientBlockId = generateId("block");
    const placeholder = buildPlaceholderEnvelope(clientBlockId, content, files, doc);
    // Sort key tracks `surfaceAt` for consistency with the homepage's
    // `surfaceAt desc` ordering. On the placeholder these are equal,
    // so this also doubles as the optimistic capture timestamp for
    // collection-membership writes.
    const sortKey = placeholder.block.surfaceAt;
    // Optimistic placeholder write. Wrapped because `writeInsert`
    // throws synchronously when the TanStack DB collection's sync
    // hasn't initialized (e.g. cold-start race where the user submits
    // before the homepage's live query has mounted), or when the
    // collection's internal state was corrupted by stale IDB-hydrated
    // envelopes from a previous bundle. Without the catch, the throw
    // becomes an unhandled rejection on the `void blockStore.add(...)`
    // call site and the placeholder silently fails to appear. Logging
    // through `Logger.error` routes to Sentry so the next failure mode
    // is debuggable instead of invisible.
    try {
        blockCollection.utils.writeInsert(placeholder);
    }
    catch (e) {
        Logger.error("blockStore.add: writeInsert failed", {
            blockId: clientBlockId,
            error: e instanceof Error ? e.message : String(e),
            errorName: e instanceof Error ? e.name : undefined,
        });
    }
    // For image drops, the placeholder envelope above already points at a
    // local `blob:` URL so the picture renders immediately. We still need
    // to (a) backfill `width/height` so `ImageBlockView` applies the
    // `--fit-contain` variant (otherwise large photos overflow the card
    // until the server-side envelope lands) and (b) eventually revoke the
    // blob URL so we don't leak it for the lifetime of the tab.
    if (placeholder.block.type === "image" && files && files[0]) {
        void hydrateImagePlaceholder(clientBlockId, files[0]);
        schedulePlaceholderBlobUrlRevoke(clientBlockId);
    }
    // Make the placeholder visible in the homepage stream right away —
    // the polling loop below upserts the real envelope under the same
    // id once the server confirms ingestion, so the stream's id list
    // never needs to be rewritten. Wrapped for the same reason as
    // `writeInsert` above — any throw here is invisible at the call site.
    try {
        mainStreamStore.prependCaptured(clientBlockId, sortKey);
    }
    catch (e) {
        Logger.error("blockStore.add: prependCaptured failed", {
            blockId: clientBlockId,
            error: e instanceof Error ? e.message : String(e),
            errorName: e instanceof Error ? e.name : undefined,
        });
    }
    // Optimistic block-index writes for every selected collection, so
    // the card appears in each open collection / stack / group view in
    // the same React commit as the omnibar close. Cards self-resolve
    // the placeholder envelope via `useById` (Design B); the polling
    // loop later swaps in the real envelope under the same id.
    if (collectionIds) {
        for (const cid of collectionIds) {
            collectionStore.addBlock(cid, clientBlockId, sortKey);
        }
    }
    // Patch the `stream.stackChildren` query cache for any selected
    // collection that's a currently-open Stack. The unfurl view reads
    // its block list from this cache (NOT `collectionBlocksCollection`),
    // so without this write the new card stays invisible inside an open
    // stack until the next refetch — the loading spinner appears to
    // hang. No-op for keys with no cache entry (regular collections, or
    // stacks the user hasn't opened), so safe to fire for every id.
    // Snapshots feed the catch-block rollback below.
    const previousStackChildrenByScope = new Map();
    if (collectionIds) {
        for (const cid of collectionIds) {
            const previous = queryClient.getQueryData([
                "stream.stackChildren",
                cid,
            ]);
            if (previous === undefined)
                continue;
            if (previous.blocks.some((env) => env.block.id === clientBlockId))
                continue;
            previousStackChildrenByScope.set(cid, previous);
            const merged = [placeholder, ...previous.blocks].sort((a, b) => b.block.surfaceAt.localeCompare(a.block.surfaceAt));
            queryClient.setQueryData(["stream.stackChildren", cid], { ...previous, blocks: merged, count: previous.count + 1 });
        }
    }
    opts?.onOptimisticInsert?.(clientBlockId);
    try {
        const { ingestText, ingestWithFiles } = await import("@/api/command-api");
        const hasFiles = files && files.length > 0;
        if (hasFiles) {
            const formData = new FormData();
            if (content)
                formData.append("content", content.substring(0, MAX_CONTENT_LENGTH));
            formData.append("channel", DEFAULT_CHANNEL);
            if (primaryCollectionId)
                formData.append("collectionId", primaryCollectionId);
            formData.append("clientBlockId", clientBlockId);
            // Multipart `doc` rides as a JSON-encoded string — form fields
            // can't carry nested objects natively. The route's multipart
            // branch parses + validates it via the same TiptapDocumentSchema
            // as the JSON branch.
            if (doc)
                formData.append("doc", JSON.stringify(doc));
            for (const file of files.slice(0, MAX_FILES)) {
                formData.append("files", file);
            }
            await ingestWithFiles(formData);
        }
        else {
            await ingestText({
                // Cap on the trimmed plaintext mirrors the server's MAX_CONTENT
                // _LENGTH; when `doc` is present the server still re-derives the
                // canonical plaintext from it, so this client-side cap is mostly
                // defence-in-depth (and preserves the legacy plaintext path).
                content: (content ?? "").substring(0, MAX_CONTENT_LENGTH),
                channel: DEFAULT_CHANNEL,
                collectionId: primaryCollectionId,
                clientBlockId,
                doc,
            });
        }
        scheduleAfterIngest(clientBlockId, additionalCollectionIds);
    }
    catch (e) {
        console.error("[blockStore.add] ingest failed:", e);
        revokePlaceholderBlobUrl(clientBlockId);
        blockCollection.utils.writeDelete(clientBlockId);
        mainStreamStore.removeId(clientBlockId);
        if (collectionIds) {
            for (const cid of collectionIds) {
                collectionStore.removeBlock(cid, clientBlockId);
            }
        }
        for (const [cid, previous] of previousStackChildrenByScope) {
            queryClient.setQueryData(["stream.stackChildren", cid], previous);
        }
    }
}
// ---------------------------------------------------------------------------
// Post-ingest scheduling
//
// After `ingest` is enqueued we need three things to happen on the
// client without the user having to refresh:
//
//   1. Additional-collection fan-out once the queue worker has had
//      time to create the stub row (`copyBlocksToCollection` only
//      requires the row to exist — it does NOT require processing
//      to be complete). One-shot delayed timer.
//   2. Hydrate the placeholder envelope with the server-processed
//      one as soon as processing finishes — URL preview, extracted
//      title/summary, tags, etc. Bounded poll on a 10s tick scoped
//      to the just-captured block. This is a deliberately narrow
//      revival of the polling that was previously killed (see
//      `hydrateCapturedBlock` for the budget rationale); the
//      previous shape polled every visible card, this only watches
//      the one the user just submitted. TODO: replace with a
//      server-push channel (WebSocket / SSE) when that infra lands.
//   3. Mark the local placeholder as terminally errored if it's
//      still pending/processing after a generous deadline, so the
//      user sees a retry/dismiss affordance instead of a forever-
//      spinning card. One-shot delayed timer. The poll above exits
//      cleanly once this flips the local status.
// ---------------------------------------------------------------------------
/**
 * Delay before firing additional-collection fan-out. Chosen to
 * comfortably exceed typical queue-enqueue → stub-insert latency
 * (queue ack + first transaction is ~hundreds of ms) so the
 * `copyBlocksToCollection` call sees the row.
 */
const POST_INGEST_FANOUT_DELAY_MS = 4_000;
/**
 * How often `hydrateCapturedBlock` re-checks the server for the
 * processed envelope. 10s is a comfortable balance between
 * perceived latency (the user just submitted; they want the URL
 * preview / title / summary to appear soon-ish) and per-submit
 * cost. Worst-case poll count is `PLACEHOLDER_TERMINAL_TIMEOUT_MS /
 * HYDRATE_POLL_INTERVAL_MS` ≈ 30 GETs per capture for a block that
 * never finishes server-side; typical captures resolve on the
 * first or second poll.
 */
const HYDRATE_POLL_INTERVAL_MS = 10_000;
/**
 * Window after which the placeholder is locally flipped to
 * `processingStatus = "errored"` if it's still pending/processing.
 * Chosen long enough to cover slow scrapers (Twitter / Instagram
 * via Apify, large page fetches, audio transcription) — the server
 * may still finish after this and a future list refetch will replace
 * the local errored state.
 */
const PLACEHOLDER_TERMINAL_TIMEOUT_MS = 5 * 60_000;
/**
 * Schedule the post-ingest fan-out, the hydration poll, and the
 * placeholder terminal-state fallback.
 */
function scheduleAfterIngest(clientBlockId, additionalCollectionIds) {
    setTimeout(() => {
        void copyToAdditionalCollectionsServerOnly(clientBlockId, additionalCollectionIds);
    }, POST_INGEST_FANOUT_DELAY_MS);
    hydrateCapturedBlock(clientBlockId);
    setTimeout(() => {
        markPlaceholderTerminallyErroredIfStuck(clientBlockId);
    }, PLACEHOLDER_TERMINAL_TIMEOUT_MS);
}
/**
 * Poll the server for the freshly-captured block's processed
 * envelope and upsert it into the cache as soon as it lands. Lets
 * the user see the real card (URL preview, extracted title, AI
 * summary, tags) replace the optimistic placeholder without a tab
 * reload or focus refetch.
 *
 * Scope is intentionally narrow — one block per capture, the one
 * the user just submitted and is staring at. This is NOT a return
 * to the previous "poll every visible card" shape that was killed
 * for fanning out ~300 GETs over 5 minutes. Per-capture budget here
 * is `PLACEHOLDER_TERMINAL_TIMEOUT_MS / HYDRATE_POLL_INTERVAL_MS`
 * (~30 GETs worst case, typically <3).
 *
 * Loop exits when:
 *   - Placeholder is no longer in the cache (`blockStore.remove`
 *     ran, or `mainStreamStore.removeId`, etc.).
 *   - Local `processingStatus` is terminal (`processed`, `failed`,
 *     `errored`). The 5-minute terminal-error timer in
 *     `markPlaceholderTerminallyErroredIfStuck` flips
 *     pending/processing → errored, so the loop is guaranteed to
 *     terminate inside the same 5-minute window even if the server
 *     never responds.
 *   - The fetched envelope's status is terminal.
 *
 * TODO: replace with a server-push channel (WebSocket / SSE)
 * subscribed to block-processed events for the current account.
 * Polling is a deliberate stopgap.
 */
function hydrateCapturedBlock(clientBlockId) {
    // Recursive `setTimeout` schedule rather than a `while (true)` loop
    // so the lint rule against constant-condition loops stays clean and
    // so each tick yields the event loop back to React between fetches
    // (the loop variant blocked on the await but kept its frame alive,
    // which matters less for a 10 s tick but is the cleaner shape).
    const tick = async () => {
        const current = blockCollection.get(clientBlockId);
        if (!current)
            return;
        const localStatus = current.block.processingStatus;
        if (localStatus !== "pending" && localStatus !== "processing")
            return;
        const env = await fetchBlockEnvelope(clientBlockId);
        if (env) {
            blockCollection.utils.writeUpsert(env);
            const status = env.block.processingStatus;
            if (status !== "pending" && status !== "processing")
                return;
        }
        setTimeout(() => void tick(), HYDRATE_POLL_INTERVAL_MS);
    };
    setTimeout(() => void tick(), HYDRATE_POLL_INTERVAL_MS);
}
/**
 * Local terminal-state fallback for placeholders that never got
 * hydrated. If the placeholder is no longer in the cache (the user
 * navigated, a list refetch already replaced it, or `remove()`
 * deleted it), this is a no-op. If a list refetch DID land a real
 * envelope under the same id, its `processingStatus` will be
 * `processed` / `failed` and we leave it alone — only `pending` /
 * `processing` placeholders get flipped.
 */
function markPlaceholderTerminallyErroredIfStuck(clientBlockId) {
    const current = blockCollection.get(clientBlockId);
    if (!current)
        return;
    const status = current.block.processingStatus;
    if (status !== "pending" && status !== "processing")
        return;
    blockCollection.utils.writeUpsert({
        ...current,
        block: {
            ...current.block,
            processingStatus: "errored",
            metadata: {
                ...current.block.metadata,
                processingError: "timeout",
            },
        },
    });
}
/**
 * Server-side fan-out for the multi-collection draft case. The ingest
 * endpoint accepts a single `collectionId` (the "primary"), so any
 * additional collections selected in the omnibar's destination chip
 * have to be persisted via a separate `copyBlocks` call once the
 * polling loop confirms the block landed.
 *
 * Optimistic UI for these additional collections is already in place:
 * `blockStore.add` wrote membership rows for every selected
 * collection in the same React commit as the placeholder envelope, so
 * the cards already appear in those collection-detail tabs. This
 * function just commits the change to the server. On failure we roll
 * back the affected membership rows.
 */
async function copyToAdditionalCollectionsServerOnly(blockId, collectionIds) {
    if (collectionIds.length === 0)
        return;
    const results = await Promise.allSettled(collectionIds.map((id) => 
    // Use the API directly (not `collectionStore.copyBlocks`) because
    // the optimistic membership rows are already in place — calling
    // `copyBlocks` would write them a second time.
    import("@/api/command-api").then(({ copyBlocksToCollection }) => copyBlocksToCollection(id, [blockId]))));
    const failures = [];
    results.forEach((r, i) => {
        if (r.status === "rejected") {
            failures.push(i);
            // Roll back the optimistic row for this specific collection so
            // the user doesn't think the copy succeeded.
            collectionStore.removeBlock(collectionIds[i], blockId);
        }
    });
    if (failures.length > 0) {
        console.warn(`[blockStore.add] failed to copy block to ${failures.length}/${collectionIds.length} additional collections`);
    }
}
// ---------------------------------------------------------------------------
// Cache-only helpers (used by side fetches: search, smart collections,
// collection detail). These intentionally do NOT touch mainStreamStore
// — that's the entire point of the split.
// ---------------------------------------------------------------------------
/**
 * Mirror a batch of pre-fetched envelopes into the block collection so
 * they're addressable by `useById` (and any other live query). This is the
 * shared seam screens like Collection detail, Smart collection, and Search
 * use when they fetch blocks via their own routes — without writing them
 * here, clicking the card sets `activeBlockId` but the modal can never
 * resolve the envelope, leaving only the dashed `--active` placeholder.
 *
 * `blockCollection` is now a manual-sync collection with `startSync: true`,
 * so the sync handles are wired before module evaluation finishes — no
 * `preload()` round-trip needed.
 */
function cacheEnvelopes(envelopes) {
    if (envelopes.length === 0)
        return;
    blockCollection.utils.writeBatch(() => {
        for (const envelope of envelopes) {
            blockCollection.utils.writeUpsert(envelope);
        }
    });
}
/**
 * Ensure specific block IDs are present in the collection.
 * Fetches any that are missing (e.g. pinned blocks outside the initial load window).
 */
async function ensureLoaded(blockIds) {
    if (blockIds.length === 0)
        return;
    const ids = blockIds.slice(0, MAX_ENSURE_LOADED);
    const existing = new Set(Array.from(blockCollection.keys()));
    const missing = ids.filter((id) => !existing.has(id));
    if (missing.length === 0)
        return;
    const results = await Promise.all(missing.map((id) => fetchBlockEnvelope(id)));
    const envelopes = results.filter((e) => e !== undefined);
    if (envelopes.length === 0)
        return;
    blockCollection.utils.writeBatch(() => {
        for (const envelope of envelopes) {
            blockCollection.utils.writeUpsert(envelope);
        }
    });
}
// ---------------------------------------------------------------------------
// Surface (bump to top)
// ---------------------------------------------------------------------------
function scrollBlockIntoView(blockId) {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            const el = document.querySelector(`[data-block-id="${blockId}"]`);
            if (!el)
                return;
            const rect = el.getBoundingClientRect();
            const y = rect.top + window.scrollY - SURFACE_SCROLL_TOPBAR_OFFSET_PX;
            window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
        });
    });
}
/**
 * Surface a block to the top of the timeline (bump).
 * Updates local `surfaceAt` immediately so the feed re-sorts, scrolls the card
 * into view, then confirms with the API (patching server time on success,
 * reverting on failure).
 */
async function surface(blockId) {
    const existing = blockCollection.get(blockId);
    if (!existing) {
        const { surfaceBlock } = await import("@/api/command-api");
        await surfaceBlock(blockId);
        return;
    }
    const previousSurfaceAt = existing.block.surfaceAt;
    const optimisticSurfaceAt = new Date().toISOString();
    blockCollection.utils.writeUpsert({
        ...existing,
        block: { ...existing.block, surfaceAt: optimisticSurfaceAt },
    });
    // The home stream reorders automatically because its `useList` orders
    // the live query by `surfaceAt` (which we just wrote). Per-collection
    // views ORDER BY a separate `sortKey` column on the index rows, so
    // that has to be bumped explicitly.
    collectionStore.bumpBlockSortKey(blockId, optimisticSurfaceAt);
    scrollBlockIntoView(blockId);
    try {
        const { surfaceBlock } = await import("@/api/command-api");
        const { surfaceAt } = await surfaceBlock(blockId);
        const current = blockCollection.get(blockId);
        if (current) {
            blockCollection.utils.writeUpsert({
                ...current,
                block: { ...current.block, surfaceAt },
            });
        }
        // Reconcile per-collection sortKey to the server-confirmed
        // timestamp. The home stream already reflects it via the envelope
        // upsert above.
        collectionStore.bumpBlockSortKey(blockId, surfaceAt);
    }
    catch {
        const current = blockCollection.get(blockId);
        if (current) {
            blockCollection.utils.writeUpsert({
                ...current,
                block: { ...current.block, surfaceAt: previousSurfaceAt },
            });
        }
        // Roll the per-collection sortKey back to its previous value. The
        // home stream reverts naturally via the envelope rollback above.
        collectionStore.bumpBlockSortKey(blockId, previousSurfaceAt);
        throw new Error("Failed to surface block");
    }
}
async function update(blockId, patch) {
    const existing = blockCollection.get(blockId);
    if (!existing) {
        // Nothing cached to update optimistically — go straight to the
        // server. The next list/details fetch will reconcile the cache.
        const { updateBlock } = await import("@/api/command-api");
        const updated = await updateBlock(blockId, patch);
        blockCollection.utils.writeUpsert({ block: updated });
        return;
    }
    // Build the optimistic envelope. Title is a top-level field on
    // the block; content needs to be merged with the existing payload
    // so a title-only edit doesn't clobber `doc`/`text` (and vice
    // versa). Server applies the same merge — keep the shapes in sync.
    const optimisticBlock = {
        ...existing.block,
        ...(patch.title !== undefined && { title: patch.title }),
        ...(patch.content !== undefined && {
            content: {
                ...existing.block.content,
                ...patch.content,
            },
        }),
    };
    const previous = existing;
    blockCollection.utils.writeUpsert({ ...existing, block: optimisticBlock });
    try {
        const { updateBlock } = await import("@/api/command-api");
        const updated = await updateBlock(blockId, patch);
        // Reconcile to the server-canonical block — the server may have
        // re-extracted plaintext from the doc, bumped updatedAt, etc.
        blockCollection.utils.writeUpsert({ ...existing, block: updated });
    }
    catch (e) {
        blockCollection.utils.writeUpsert(previous);
        throw e;
    }
}
// ---------------------------------------------------------------------------
// Toggle task-list checkbox (in-place, no edit-mode entry)
//
// View-only surfaces (homepage stream cards, block detail modal in read
// mode) let users tick / untick task-list checkboxes without opening the
// edit shell. This function owns the full lifecycle of a single toggle:
//
//   1. Build the new doc by mutating the Nth `taskItem` (depth-first
//      document order — matches the index `TiptapRender` allocates when
//      it walks the same doc).
//   2. Optimistically write the patched envelope into the cache so the
//      checkbox flips instantly.
//   3. Debounce the actual server save by `TASK_TOGGLE_DEBOUNCE_MS`. The
//      user toggling several boxes in rapid succession bundles into a
//      single PATCH after they stop clicking. Per-block — different
//      blocks toggle independently.
//   4. When the debounce fires, claim → PATCH → release the edit lock.
//      Saves are serialised per-block via `taskTogglePersistence` so a
//      second debounce that fires while the first save is still
//      in-flight chains AFTER it rather than tripping on the lock
//      contention.
//   5. On claim/save failure surface a toast and log to Sentry. The
//      optimistic cache state is intentionally LEFT in place — the
//      user's intended state is still on screen, and a subsequent
//      toggle (or app refresh) reconciles. Rolling back would erase
//      every box they ticked in the burst, which is worse UX than a
//      stale "couldn't save" toast on a still-visible toggle.
// ---------------------------------------------------------------------------
/** Per-block debounce timer ids. `window.setTimeout` returns `number`. */
const taskToggleTimers = new Map();
/**
 * Per-block in-flight save promise. Used to chain a second save behind
 * the first so two debounces firing inside one server round-trip don't
 * contend on the edit lock.
 */
const taskTogglePersistence = new Map();
/**
 * Per-block "last successfully saved doc" reference. When the debounce
 * fires and the cache doc is still referentially equal to this, we skip
 * the redundant PATCH — common when a save races a click that fires
 * mid-claim and the freshest-read inside `persistTaskToggles` already
 * picked up the new state.
 */
const taskToggleLastSaved = new Map();
/**
 * Debounce delay between the last task toggle and the actual server
 * save. Tuned for "click several boxes in a row" UX — long enough that
 * the typical multi-toggle burst lands in one PATCH, short enough that
 * the user sees their work persist quickly after they stop.
 */
const TASK_TOGGLE_DEBOUNCE_MS = 800;
/**
 * Pure helper. Returns a new `TiptapDocument` with the Nth task item's
 * `checked` attr set to `nextChecked`, or `undefined` when the index is
 * out of range (no-op — caller should bail). The walk order matches
 * `TiptapRender`'s depth-first pre-order task counter so consumers and
 * the renderer agree on which task item index N refers to.
 *
 * Returns `undefined` (rather than the unchanged input) so callers can
 * distinguish "no change" from "change applied" with a single `=== undefined`
 * check; comparing doc references after `.map` won't catch the no-op
 * case because every container node is rebuilt during the walk.
 */
function toggleTaskInDoc(doc, targetIndex, nextChecked) {
    const cursor = { value: 0 };
    let didChange = false;
    function modify(node) {
        let result = node;
        if (node.type === "taskItem") {
            const idx = cursor.value++;
            if (idx === targetIndex) {
                didChange = true;
                result = {
                    ...node,
                    attrs: { ...(node.attrs ?? {}), checked: nextChecked },
                };
            }
        }
        // Walk children unconditionally so nested task lists keep
        // contributing to the cursor — a deeply-nested target at index 7
        // requires the outer task items to advance the counter past 0..6.
        if (result.content) {
            result = { ...result, content: result.content.map(modify) };
        }
        return result;
    }
    const newContent = doc.content?.map(modify);
    if (!didChange)
        return undefined;
    return { ...doc, content: newContent };
}
/**
 * Toggle the Nth task item's `checked` state and persist the change
 * (debounced). Safe to call repeatedly in fast succession — see the
 * section header for the full lifecycle.
 *
 * No-ops silently for:
 *   - blocks not in cache (the caller must already be looking at one
 *     to have a checkbox to click);
 *   - non-text blocks;
 *   - blocks without a rich `doc` (a plaintext-only task list can't
 *     exist — TaskItem is a doc node);
 *   - `taskIndex` out of range (the doc's task count changed between
 *     the renderer walking it and the click landing).
 */
function toggleTaskItem(blockId, taskIndex, nextChecked) {
    const existing = blockCollection.get(blockId);
    if (!existing)
        return;
    if (existing.block.type !== "text")
        return;
    const content = existing.block.content;
    if (!content.doc)
        return;
    const nextDoc = toggleTaskInDoc(content.doc, taskIndex, nextChecked);
    if (nextDoc === undefined)
        return;
    // Optimistic cache write — the checkbox flips immediately.
    blockCollection.utils.writeUpsert({
        ...existing,
        block: {
            ...existing.block,
            content: { ...content, doc: nextDoc },
        },
    });
    // Restart debounce. Clearing the previous timer (rather than letting
    // both fire) bundles back-to-back clicks into a single PATCH.
    const existingTimer = taskToggleTimers.get(blockId);
    if (existingTimer !== undefined) {
        window.clearTimeout(existingTimer);
    }
    const timer = window.setTimeout(() => {
        taskToggleTimers.delete(blockId);
        void persistTaskToggles(blockId);
    }, TASK_TOGGLE_DEBOUNCE_MS);
    taskToggleTimers.set(blockId, timer);
}
/**
 * Force any pending task-toggle debounce on `blockId` to run NOW (and
 * waits for it to complete), so the caller can safely proceed with a
 * code path that mutates the same block.
 *
 * The motivating case is the block-detail modal's "enter edit mode"
 * flow: if the user toggled a checkbox in view mode 600ms ago, the
 * 800ms debounce is still pending; without a flush, the debounce
 * fires AFTER the editor has claimed its edit lock, succeeds as a
 * renewal (same account), then releases the lock at the end of its
 * PATCH cycle — kicking the user out of edit mode 30s later when
 * their heartbeat fails.
 *
 * Resolves once the pending save (if any) has finished. No-ops when
 * there's nothing pending. Errors from the underlying save are
 * surfaced as toasts inside `persistTaskToggles` and intentionally
 * not re-thrown here — the caller is interested in "is it safe to
 * proceed", which is true either way.
 */
async function flushPendingTaskToggle(blockId) {
    const timer = taskToggleTimers.get(blockId);
    if (timer !== undefined) {
        // A toggle was queued but the debounce hasn't fired yet — cancel
        // the timer and run the persist synchronously so the lock state
        // is settled before the caller proceeds. `persistTaskToggles`
        // chains after any in-flight save on this block, so awaiting it
        // covers both "timer pending" and "timer + in-flight prior save"
        // in one shot.
        window.clearTimeout(timer);
        taskToggleTimers.delete(blockId);
        await persistTaskToggles(blockId);
        return;
    }
    // No pending timer — wait for any in-flight save to finish so the
    // caller's lock claim doesn't race with this block's currently-
    // running PATCH/release.
    const inFlight = taskTogglePersistence.get(blockId);
    if (inFlight) {
        await inFlight.catch(() => undefined);
    }
}
/**
 * Send the current cached doc to the server. Owns claim → PATCH →
 * release, plus error surfacing. Serialised per-block via
 * `taskTogglePersistence` so two debounces firing inside one save
 * round-trip don't race for the lock.
 */
async function persistTaskToggles(blockId) {
    const prior = taskTogglePersistence.get(blockId);
    const work = (async () => {
        // Chain after any prior save on this block so the second debounce
        // sees a clean lock state. Swallow the prior error — it was
        // already surfaced by that save's own catch branch and we don't
        // want it to skip this one.
        if (prior) {
            await prior.catch(() => undefined);
        }
        // Read the cache HERE (not at debounce-fire time) so a click that
        // landed during the prior save is included in this save's PATCH.
        const current = blockCollection.get(blockId);
        if (!current)
            return;
        if (current.block.type !== "text")
            return;
        const currentContent = current.block.content;
        if (!currentContent.doc)
            return;
        // No-op skip: nothing to save if the cache doc matches the doc we
        // last successfully persisted. Saves a round-trip + lock-claim on
        // the "second debounce after a save that already captured all
        // toggles" race.
        const lastSaved = taskToggleLastSaved.get(blockId);
        if (lastSaved !== undefined && lastSaved === currentContent.doc)
            return;
        const [{ claimBlockLock, releaseBlockLock, updateBlock, LockLostError }, { showToast },] = await Promise.all([
            import("@/api/command-api"),
            import("@shared/client/components/toast/show-toast"),
        ]);
        const claim = await claimBlockLock(blockId);
        if (claim.ok === false) {
            // Concurrent-edit conflict reads as a normal warning — the user
            // sees the offending editor's name so they know why their click
            // didn't save. Other reasons (forbidden, network) get the
            // generic error variant + Sentry-via-Logger.
            if (claim.reason === "held-by-other") {
                showToast.warning(`Couldn't save — ${claim.existing.accountDisplayName} is editing this block`);
            }
            else {
                showToast.error("Couldn't save changes");
                Logger.error("blockStore.toggleTaskItem: claim failed", {
                    blockId,
                    reason: claim.reason,
                    message: claim.message,
                });
            }
            return;
        }
        try {
            // Re-read cache one more time right before PATCH so we pick up
            // any toggle that landed during the (potentially slow) claim
            // round-trip. Falls back to the doc we read at function entry
            // if the cache disappeared mid-flight (unlikely but defensive).
            const freshest = blockCollection.get(blockId);
            const freshContent = freshest?.block.content;
            const docToSave = freshContent?.doc ?? currentContent.doc;
            await updateBlock(blockId, {
                content: { doc: docToSave },
            });
            taskToggleLastSaved.set(blockId, docToSave);
        }
        catch (e) {
            if (e instanceof LockLostError) {
                showToast.warning("Couldn't save — edit lock was taken over");
            }
            else {
                showToast.error("Couldn't save changes");
            }
            Logger.error("blockStore.toggleTaskItem: update failed", {
                blockId,
                error: e instanceof Error ? e.message : String(e),
            });
        }
        finally {
            // Fire-and-forget release — DELETE is idempotent server-side so
            // a network hiccup just means the TTL clears the lock instead
            // of an explicit DELETE.
            void releaseBlockLock(blockId);
        }
    })();
    taskTogglePersistence.set(blockId, work);
    try {
        await work;
    }
    finally {
        // Only clear the map slot if we're still the latest in-flight save
        // for this block — a subsequent toggle that scheduled a new save
        // before this `finally` ran would have already overwritten us.
        if (taskTogglePersistence.get(blockId) === work) {
            taskTogglePersistence.delete(blockId);
        }
    }
}
// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------
async function remove(blockId) {
    blockCollection.utils.writeDelete(blockId);
    // Drop from every per-scope index in lockstep so the homepage,
    // every open collection / stack / group view, smart-collection
    // tabs, and the pinned section all lose the card in the same React
    // commit. None of these calls round-trip to the server — they're
    // pure in-memory pointer drops.
    mainStreamStore.removeId(blockId);
    collectionStore.removeBlockEverywhere(blockId);
    const { deleteBlock } = await import("@/api/command-api");
    const { pinnedStreamStore } = await import("@/data/pinned-stream-store");
    await deleteBlock(blockId);
    await pinnedStreamStore.unpinIfPresent(blockId);
}
// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const blockStore = {
    useById,
    get,
    upsert,
    upsertMany,
    delete: deleteFromCache,
    add,
    surface,
    update,
    toggleTaskItem,
    flushPendingTaskToggle,
    remove,
    cacheEnvelopes,
    ensureLoaded,
    scrollBlockIntoView,
};
